import {
  Box, Card, CardContent, Typography, Grid, TextField, Button, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, LinearProgress,
  Divider, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Avatar, Stack, Paper,
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
  TrendingUp, Inventory, People, AccountBalance, Assessment,
  PointOfSale, CreditCard, PieChart as PieIcon, Store, Person,
  TableChart, PictureAsPdf, CalendarToday, Warning, AttachMoney,
} from '@mui/icons-material';
import { useState, useCallback } from 'react';
import api from '../../api/axios';

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const fmt  = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const pct  = (n) => `${(+n || 0).toFixed(1)}%`;
const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#3b82f6','#ec4899','#14b8a6','#f97316'];

const downloadExport = async (path, filename) => {
  const auth = JSON.parse(localStorage.getItem('pos_auth') || '{}');
  const token = auth.accessToken;
  if (!token) { alert('Session expired'); return; }
  const res = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}${path}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) { const e = await res.json().catch(() => ({})); alert(`Export failed: ${e.message || res.statusText}`); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ─── sidebar items ─────────────────────────────────────────────────────────── */
const REPORTS = [
  { key: 'sales',       label: 'Sales Report',         icon: <TrendingUp />,     color: '#6366f1' },
  { key: 'pl',          label: 'Profit & Loss',         icon: <AttachMoney />,    color: '#22c55e' },
  { key: 'inventory',   label: 'Inventory Valuation',   icon: <Inventory />,      color: '#f59e0b' },
  { key: 'eod',         label: 'End of Day',            icon: <CalendarToday />,  color: '#3b82f6' },
  { key: 'cashflow',    label: 'Cash Flow',             icon: <AccountBalance />, color: '#14b8a6' },
  { key: 'gst',         label: 'GST / Tax Report',      icon: <Assessment />,     color: '#8b5cf6' },
  { key: 'margins',     label: 'Product Margins',       icon: <PieIcon />,        color: '#ec4899' },
  { key: 'category',    label: 'Sales by Category',     icon: <Store />,          color: '#f97316' },
  { key: 'staff',       label: 'Staff Performance',     icon: <Person />,         color: '#10b981' },
  { key: 'customers',   label: 'Customer Report',       icon: <People />,         color: '#0ea5e9' },
  { key: 'aging',       label: 'Receivables Aging',     icon: <CreditCard />,     color: '#ef4444' },
  { key: 'expenses',    label: 'Expense Report',        icon: <PointOfSale />,    color: '#d97706' },
];

/* ─── stat card ─────────────────────────────────────────────────────────────── */
function StatCard({ label, value, color = '#6366f1', sub }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <Typography variant="h5" fontWeight={800} color={color} sx={{ whiteSpace: 'nowrap' }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" mt={0.3}>{label}</Typography>
      {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
    </Paper>
  );
}

/* ─── date filter bar ───────────────────────────────────────────────────────── */
function DateBar({ from, to, setFrom, setTo, onLoad, loading, exportKey, extraFilters }) {
  return (
    <Card sx={{ mb: 2.5 }}>
      <CardContent sx={{ py: '12px !important' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To"   type="date" value={to}   onChange={e => setTo(e.target.value)}   InputLabelProps={{ shrink: true }} />
          {extraFilters}
          <Button variant="contained" onClick={onLoad} disabled={loading} sx={{ minWidth: 100 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Generate'}
          </Button>
          {exportKey && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<TableChart />}
                onClick={() => downloadExport(`/export/excel/${exportKey}?from=${from}&to=${to}`, `${exportKey}-${Date.now()}.xlsx`)}>
                Excel
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdf />}
                onClick={() => downloadExport(`/export/pdf/${exportKey}?from=${from}&to=${to}`, `${exportKey}-${Date.now()}.pdf`)}>
                PDF
              </Button>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

/* ════════════════════════════════════════════════════════════════════════════ */
/*  Individual report panels                                                     */
/* ════════════════════════════════════════════════════════════════════════════ */

function SalesReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/sales', { params: { from, to } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} exportKey="sales" />
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Revenue"   value={`₨ ${fmt(data.totals?.total_revenue)}`}   color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Invoices"  value={data.totals?.total_invoices}              color="#22c55e" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Discounts" value={`₨ ${fmt(data.totals?.total_discounts)}`} color="#f59e0b" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Credit/Due"      value={`₨ ${fmt(data.summary?.reduce((s,r)=>s+(+r.credit||0),0))}`} color="#ef4444" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Daily Revenue</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={[...data.summary].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={70} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar dataKey="discounts" name="Discounts" fill="#f59e0b" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discounts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Tax</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Credit</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.summary?.map((r,i) => (
                  <TableRow key={i} hover>
                    <TableCell>{r.date}</TableCell>
                    <TableCell align="right">{r.invoices}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.revenue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>₨ {fmt(r.discounts)}</TableCell>
                    <TableCell align="right">₨ {fmt(r.tax)}</TableCell>
                    <TableCell align="right" sx={{ color: r.credit > 0 ? 'error.main' : 'inherit' }}>₨ {fmt(r.credit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function PLReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/profit-loss', { params: { from, to } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  const rows = data ? [
    { label: 'Gross Revenue',     value: data.revenue,       color: '#22c55e', indent: 0, bold: true },
    { label: 'Cost of Goods Sold (COGS)', value: -data.cogs, color: '#ef4444', indent: 1, bold: false },
    { label: 'Gross Profit',      value: data.gross_profit,  color: data.gross_profit >= 0 ? '#22c55e' : '#ef4444', indent: 0, bold: true, divider: true },
    { label: 'Operating Expenses',value: -data.expenses,     color: '#ef4444', indent: 1, bold: false },
    { label: 'Net Profit / Loss', value: data.net_profit,    color: data.net_profit >= 0 ? '#22c55e' : '#ef4444', indent: 0, bold: true, divider: true },
  ] : [];
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} exportKey="sales" />
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Revenue"      value={`₨ ${fmt(data.revenue)}`}      color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="COGS"         value={`₨ ${fmt(data.cogs)}`}         color="#ef4444" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Gross Profit" value={`₨ ${fmt(data.gross_profit)}`} color={data.gross_profit>=0?'#22c55e':'#ef4444'} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Net Profit"   value={`₨ ${fmt(data.net_profit)}`}   color={data.net_profit>=0?'#22c55e':'#ef4444'} /></Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>P&L Statement</Typography>
                  <Table>
                    <TableBody>
                      {rows.map((r, i) => (
                        <>
                          {r.divider && <TableRow key={`d${i}`}><TableCell colSpan={2} sx={{ p: 0 }}><Divider /></TableCell></TableRow>}
                          <TableRow key={i}>
                            <TableCell sx={{ pl: r.indent ? 4 : 2 }}>
                              <Typography fontWeight={r.bold ? 700 : 400} fontSize={r.bold ? 15 : 14}>{r.label}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={r.bold ? 700 : 500} color={r.color} fontSize={r.bold ? 15 : 14}>
                                {r.value < 0 ? '- ' : ''}₨ {fmt(Math.abs(r.value))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Breakdown</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={[{ name: 'COGS', value: Math.abs(data.cogs) }, { name: 'Expenses', value: Math.abs(data.expenses) }, { name: 'Net Profit', value: Math.max(0, data.net_profit) }]}
                        dataKey="value" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                        {['#ef4444','#f59e0b','#22c55e'].map((c,i) => <Cell key={i} fill={c} />)}
                      </Pie>
                      <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

function InventoryReport() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/inventory'); setData(r.data.data); }
    finally { setLoading(false); }
  }, []);
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
        <Button variant="contained" onClick={load} disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Load Report'}
        </Button>
        <Button size="small" variant="outlined" startIcon={<TableChart />}
          onClick={() => downloadExport('/export/excel/inventory', `inventory-${Date.now()}.xlsx`)}>Excel</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdf />}
          onClick={() => downloadExport('/export/pdf/inventory', `inventory-${Date.now()}.pdf`)}>PDF</Button>
      </Box>
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Products" value={data.rows?.length}                    color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Stock Value" value={`₨ ${fmt(data.total_value)}`}     color="#22c55e" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Zero Stock Items"  value={data.rows?.filter(r=>r.stock===0).length} color="#ef4444" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Avg Item Value"    value={`₨ ${fmt(data.total_value / (data.rows?.length||1))}`} color="#f59e0b" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Top 10 by Stock Value</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.rows?.slice(0,10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="stock_value" name="Value" fill="#6366f1" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small" stickyHeader>
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Stock</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Cost Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Sale Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Stock Value</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Potential Revenue</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.rows?.map(r => (
                  <TableRow key={r.id} hover sx={{ opacity: r.stock === 0 ? 0.5 : 1 }}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{r.sku}</Typography></TableCell>
                    <TableCell align="right">
                      <Chip label={r.stock} size="small" color={r.stock === 0 ? 'error' : r.stock <= 5 ? 'warning' : 'success'} />
                    </TableCell>
                    <TableCell align="right">₨ {fmt(r.purchase_price)}</TableCell>
                    <TableCell align="right">₨ {fmt(r.sale_price)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.stock_value)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>₨ {fmt(r.stock * r.sale_price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function EODReport() {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/eod', { params: { date } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [date]);
  return (
    <>
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField size="small" label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={load} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Generate EOD'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      {data && (
        <>
          <Alert severity={data.sales.count > 0 ? 'success' : 'info'} sx={{ mb: 2.5 }}>
            EOD Report for <strong>{date}</strong> — {data.sales.count} invoice(s) processed
          </Alert>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={2.4}><StatCard label="Total Revenue"  value={`₨ ${fmt(data.sales.revenue)}`}   color="#6366f1" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Cash Collected" value={`₨ ${fmt(data.sales.collected)}`} color="#22c55e" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Cash Sales"     value={`₨ ${fmt(data.sales.cash_sales)}`} color="#3b82f6" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Card Sales"     value={`₨ ${fmt(data.sales.card_sales)}`} color="#8b5cf6" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Credit / Due"   value={`₨ ${fmt(data.sales.credit)}`}    color="#ef4444" /></Grid>
          </Grid>
          <Grid container spacing={2.5} mb={2.5}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Hourly Sales</Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={data.hourly}>
                      <defs>
                        <linearGradient id="hourGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="hour" tickFormatter={h => `${h}:00`} tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={70} />
                      <Tooltip formatter={v => `₨ ${fmt(v)}`} labelFormatter={h => `${h}:00`} />
                      <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#hourGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Payment Mix</Typography>
                  <Stack spacing={1.5}>
                    {[
                      { label: 'Cash', val: data.sales.cash_sales, color: '#22c55e' },
                      { label: 'Card', val: data.sales.card_sales, color: '#3b82f6' },
                      { label: 'Bank Transfer', val: data.sales.bank_sales, color: '#8b5cf6' },
                      { label: 'Credit / Due', val: data.sales.credit, color: '#ef4444' },
                    ].map(m => {
                      const total = data.sales.revenue || 1;
                      return (
                        <Box key={m.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                            <Typography variant="caption" fontWeight={700}>₨ {fmt(m.val)}</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(100, (m.val/total)*100)}
                            sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: m.color } }} />
                        </Box>
                      );
                    })}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">Expenses: <strong>₨ {fmt(data.expenses.amount)}</strong></Typography>
                  <Typography variant="body2" color="text.secondary">Returns: <strong>₨ {fmt(data.returns.amount)}</strong></Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Top 5 Products Today</Typography>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Qty Sold</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.topProducts?.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell><Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: COLORS[i] }}>{i+1}</Avatar></TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell align="right">{p.qty_sold}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(p.revenue)}</TableCell>
                    </TableRow>
                  ))}
                  {!data.topProducts?.length && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No sales on this date</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}

function CashFlowReport() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/cashflow', { params: { months } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [months]);
  return (
    <>
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Period</InputLabel>
              <Select value={months} onChange={e => setMonths(e.target.value)} label="Period">
                <MenuItem value={3}>Last 3 months</MenuItem>
                <MenuItem value={6}>Last 6 months</MenuItem>
                <MenuItem value={12}>Last 12 months</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={load} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Generate'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Revenue"  value={`₨ ${fmt(data.reduce((s,r)=>s+r.revenue,0))}`}   color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Expenses" value={`₨ ${fmt(data.reduce((s,r)=>s+r.expenses,0))}`}  color="#ef4444" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Net Cash Flow"  value={`₨ ${fmt(data.reduce((s,r)=>s+r.profit,0))}`}   color="#22c55e" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Avg Monthly Revenue" value={`₨ ${fmt(data.reduce((s,r)=>s+r.revenue,0)/(data.length||1))}`} color="#f59e0b" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Revenue vs Expenses vs Profit</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data}>
                  <defs>
                    {[['revGrad','#6366f1'],['expGrad','#ef4444'],['profGrad','#22c55e']].map(([id,c]) => (
                      <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={c} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={c} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={75} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue"  name="Revenue"  stroke="#6366f1" fill="url(#revGrad)"  strokeWidth={2} />
                  <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" fill="url(#expGrad)"  strokeWidth={2} />
                  <Area type="monotone" dataKey="profit"   name="Profit"   stroke="#22c55e" fill="url(#profGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Expenses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Net Profit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Margin</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.month}</TableCell>
                    <TableCell align="right">₨ {fmt(r.revenue)}</TableCell>
                    <TableCell align="right">₨ {fmt(r.collected)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>₨ {fmt(r.expenses)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: r.profit >= 0 ? 'success.main' : 'error.main' }}>₨ {fmt(r.profit)}</TableCell>
                    <TableCell align="right">
                      <Chip label={r.revenue ? pct((r.profit/r.revenue)*100) : '0%'} size="small"
                        color={r.profit >= 0 ? 'success' : 'error'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function GSTReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState('month');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/gst', { params: { from, to, group_by: groupBy } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to, groupBy]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading}
        extraFilters={
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Group By</InputLabel>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)} label="Group By">
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
            </Select>
          </FormControl>
        }
      />
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Tax Collected" value={`₨ ${fmt(data.totals?.total_tax)}`}      color="#8b5cf6" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Revenue"       value={`₨ ${fmt(data.totals?.total_revenue)}`}  color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Discounts"     value={`₨ ${fmt(data.totals?.total_discounts)}`} color="#f59e0b" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Invoices"      value={data.totals?.total_invoices}              color="#22c55e" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Tax Collected Over Time</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[...data.rows].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={75} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="tax_collected" name="GST/Tax" fill="#8b5cf6" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discounts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Tax (GST)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total incl. Tax</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.rows?.map((r,i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.period}</TableCell>
                    <TableCell align="right">{r.invoices}</TableCell>
                    <TableCell align="right">₨ {fmt(r.subtotal)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>₨ {fmt(r.discounts)}</TableCell>
                    <TableCell align="right" sx={{ color: '#8b5cf6', fontWeight: 700 }}>₨ {fmt(r.tax_collected)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.total_with_tax)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function MarginsReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/margins', { params: { from, to, limit: 100 } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} />
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Revenue"  value={`₨ ${fmt(data.summary?.total_revenue)}`}  color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total COGS"     value={`₨ ${fmt(data.summary?.total_cogs)}`}     color="#ef4444" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Gross Profit"   value={`₨ ${fmt(data.summary?.total_profit)}`}   color="#22c55e" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Avg Margin"     value={`${data.summary?.avg_margin}%`}           color="#f59e0b" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Top 10 Products by Profit</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.rows?.slice(0,10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="gross_profit" name="Gross Profit" fill="#22c55e" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small" stickyHeader>
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Units Sold</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>COGS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Profit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Margin %</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.rows?.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{r.sku}</Typography>
                    </TableCell>
                    <TableCell>{r.category || '—'}</TableCell>
                    <TableCell align="right">{r.units_sold}</TableCell>
                    <TableCell align="right">₨ {fmt(r.revenue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>₨ {fmt(r.cogs)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: r.gross_profit >= 0 ? 'success.main' : 'error.main' }}>₨ {fmt(r.gross_profit)}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${r.margin_pct || 0}%`} size="small"
                        color={r.margin_pct >= 20 ? 'success' : r.margin_pct >= 10 ? 'warning' : 'error'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function CategoryReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/by-category', { params: { from, to } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} />
      {data && (
        <>
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Revenue by Category</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={data} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={100}
                        label={({ category, percent }) => `${category} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>Units Sold by Category</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={110} />
                      <Tooltip />
                      <Bar dataKey="units_sold" name="Units" fill="#f97316" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Card>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Units Sold</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>COGS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Profit</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length], flexShrink: 0 }} />
                        {r.category}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{r.invoices}</TableCell>
                    <TableCell align="right">{r.units_sold}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.revenue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>₨ {fmt(r.cogs)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: r.profit >= 0 ? 'success.main' : 'error.main' }}>₨ {fmt(r.profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function StaffReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/staff', { params: { from, to } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} />
      {data && (
        <>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Revenue per Cashier</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.filter(r => r.total_revenue > 0)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={75} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="total_revenue" name="Revenue" fill="#10b981" radius={[4,4,0,0]} />
                  <Bar dataKey="total_discounts" name="Discounts" fill="#f59e0b" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Staff</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discounts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Credit Given</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Sale</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Sale</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#10b981' }}>{r.name?.[0]}</Avatar>
                        {r.name}
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={r.role} size="small" /></TableCell>
                    <TableCell align="right">{r.total_invoices}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.total_revenue)}</TableCell>
                    <TableCell align="right">₨ {fmt(r.total_collected)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>₨ {fmt(r.total_discounts)}</TableCell>
                    <TableCell align="right" sx={{ color: r.total_credit > 0 ? 'error.main' : 'inherit' }}>₨ {fmt(r.total_credit)}</TableCell>
                    <TableCell align="right">₨ {fmt(r.avg_sale)}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.last_sale_at ? new Date(r.last_sale_at).toLocaleDateString('en-PK') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function CustomerReport() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/customers'); setData(r.data.data); }
    finally { setLoading(false); }
  }, []);
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
        <Button variant="contained" onClick={load} disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Load Report'}
        </Button>
      </Box>
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Total Customers"   value={data.length}                                                  color="#0ea5e9" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Spent (All)" value={`₨ ${fmt(data.reduce((s,r)=>s+(+r.total_spent||0),0))}`}       color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Active (with orders)" value={data.filter(r=>r.total_orders>0).length}                    color="#22c55e" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Total Outstanding"  value={`₨ ${fmt(data.reduce((s,r)=>s+(+r.credit_balance||0),0))}`}  color="#ef4444" /></Grid>
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Top 10 Customers by Spend</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.slice(0,10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Bar dataKey="total_spent" name="Total Spent" fill="#0ea5e9" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small" stickyHeader>
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Orders</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total Spent</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.map((r, i) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ color: 'text.disabled', width: 40 }}>{i+1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: COLORS[i%COLORS.length] }}>{r.name?.[0]}</Avatar>
                        {r.name}
                      </Box>
                    </TableCell>
                    <TableCell>{r.phone || '—'}</TableCell>
                    <TableCell align="right">{r.total_orders}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.total_spent)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: r.credit_balance > 0 ? 700 : 400, color: r.credit_balance > 0 ? 'error.main' : 'inherit' }}>
                      {r.credit_balance > 0 ? `₨ ${fmt(r.credit_balance)}` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function AgingReport() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/aging'); setData(r.data.data); }
    finally { setLoading(false); }
  }, []);
  const buckets = [
    { key: 'bucket_30',     label: '0-30 days',   color: '#22c55e' },
    { key: 'bucket_60',     label: '31-60 days',  color: '#f59e0b' },
    { key: 'bucket_90',     label: '61-90 days',  color: '#f97316' },
    { key: 'bucket_over90', label: 'Over 90 days',color: '#ef4444' },
  ];
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
        <Button variant="contained" onClick={load} disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Load Report'}
        </Button>
      </Box>
      {data && (
        <>
          {data.totals?.grand_total > 0 && (
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2.5 }}>
              Total outstanding receivables: <strong>₨ {fmt(data.totals?.grand_total)}</strong>
            </Alert>
          )}
          <Grid container spacing={2} mb={3}>
            {buckets.map(b => (
              <Grid item xs={6} md={3} key={b.key}>
                <StatCard label={b.label} value={`₨ ${fmt(data.totals?.[b.key])}`} color={b.color} />
              </Grid>
            ))}
          </Grid>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Aging Buckets by Customer</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.rows?.slice(0,12)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${fmt(v)}`} width={75} />
                  <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                  <Legend />
                  {buckets.map(b => <Bar key={b.key} dataKey={b.key} name={b.label} stackId="a" fill={b.color} />)}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <Table size="small" stickyHeader>
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#22c55e' }}>0-30d</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#f59e0b' }}>31-60d</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#f97316' }}>61-90d</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#ef4444' }}>90d+</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total Due</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Sale</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.rows?.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>{r.phone}</TableCell>
                    <TableCell align="right" sx={{ color: '#22c55e' }}>{r.bucket_30 > 0 ? `₨ ${fmt(r.bucket_30)}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#f59e0b' }}>{r.bucket_60 > 0 ? `₨ ${fmt(r.bucket_60)}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#f97316' }}>{r.bucket_90 > 0 ? `₨ ${fmt(r.bucket_90)}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef4444', fontWeight: r.bucket_over90 > 0 ? 700 : 400 }}>{r.bucket_over90 > 0 ? `₨ ${fmt(r.bucket_over90)}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main' }}>₨ {fmt(r.credit_balance)}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.last_sale_at ? new Date(r.last_sale_at).toLocaleDateString('en-PK') : '—'}</TableCell>
                  </TableRow>
                ))}
                {!data.rows?.length && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No outstanding receivables 🎉</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function ExpenseReport() {
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/reports/expenses', { params: { from, to } }); setData(r.data.data); }
    finally { setLoading(false); }
  }, [from, to]);
  return (
    <>
      <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onLoad={load} loading={loading} />
      {data && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}><StatCard label="Grand Total"     value={`₨ ${fmt(data.grand_total)}`}          color="#d97706" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Categories"      value={data.rows?.length}                      color="#6366f1" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Largest Category" value={data.rows?.[0]?.category || '—'}       color="#ef4444" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Largest Amount"   value={`₨ ${fmt(data.rows?.[0]?.total)}`}    color="#f59e0b" /></Grid>
          </Grid>
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>By Category</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={data.rows} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={100}
                        label={({ category, percent }) => `${category?.split(' ')[0]} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {data.rows?.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>% Share</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {data.rows?.map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i%COLORS.length] }} />
                            {r.category || 'Uncategorized'}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>₨ {fmt(r.total)}</TableCell>
                        <TableCell align="right">
                          <Chip label={`${((r.total / data.grand_total) * 100).toFixed(1)}%`} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>₨ {fmt(data.grand_total)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════════ */
/*  Main Reports Hub                                                             */
/* ════════════════════════════════════════════════════════════════════════════ */

const PANELS = {
  sales:     SalesReport,
  pl:        PLReport,
  inventory: InventoryReport,
  eod:       EODReport,
  cashflow:  CashFlowReport,
  gst:       GSTReport,
  margins:   MarginsReport,
  category:  CategoryReport,
  staff:     StaffReport,
  customers: CustomerReport,
  aging:     AgingReport,
  expenses:  ExpenseReport,
};

export default function ReportsPage() {
  const [active, setActive] = useState('sales');
  const Panel = PANELS[active];
  const meta  = REPORTS.find(r => r.key === active);

  return (
    <Box sx={{ display: 'flex', gap: 0, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* ── sidebar ── */}
      <Box sx={{
        width: 220, flexShrink: 0, bgcolor: 'white', borderRight: '1px solid', borderColor: 'divider',
        pt: 2, pb: 2, position: 'sticky', top: 0, maxHeight: '100vh', overflowY: 'auto',
      }}>
        <Typography variant="overline" sx={{ px: 2, mb: 1, display: 'block', color: 'text.disabled', fontWeight: 700 }}>
          Reports
        </Typography>
        {REPORTS.map(r => (
          <Box key={r.key}
            onClick={() => setActive(r.key)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1, cursor: 'pointer',
              bgcolor: active === r.key ? `${r.color}15` : 'transparent',
              borderRight: active === r.key ? `3px solid ${r.color}` : '3px solid transparent',
              '&:hover': { bgcolor: active === r.key ? `${r.color}15` : 'grey.50' },
              transition: 'all .15s',
            }}
          >
            <Box sx={{ color: active === r.key ? r.color : 'text.disabled', display: 'flex', '& svg': { fontSize: 18 } }}>
              {r.icon}
            </Box>
            <Typography variant="body2" fontWeight={active === r.key ? 700 : 400} color={active === r.key ? r.color : 'text.primary'} sx={{ fontSize: 13 }}>
              {r.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── content ── */}
      <Box sx={{ flex: 1, minWidth: 0, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box sx={{ p: 1, bgcolor: `${meta.color}20`, borderRadius: 1.5, color: meta.color, display: 'flex', '& svg': { fontSize: 22 } }}>
            {meta.icon}
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800}>{meta.label}</Typography>
            <Typography variant="caption" color="text.secondary">Generate, filter and export</Typography>
          </Box>
        </Box>
        <Panel key={active} />
      </Box>
    </Box>
  );
}
